import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import { regularizationService, getErrorMessage } from '../../../services/apiService';

const VendorSelectionModal = ({ onClose, onConfirm }) => {
  const toast = useToast();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEligibleVendors();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = vendors.filter(
        (v) =>
          v.company_name.toLowerCase().includes(search.toLowerCase()) ||
          v.vendor_code?.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [search, vendors]);

  const fetchEligibleVendors = async () => {
    try {
      setLoading(true);
      const response = await regularizationService.getEligibleVendors();
      if (response.success) {
        setVendors(response.data || []);
        setFilteredVendors(response.data || []);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to fetch eligible vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVendor = (vendorId) => {
    setSelectedVendors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vendorId)) {
        newSet.delete(vendorId);
      } else {
        newSet.add(vendorId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedVendors.size === filteredVendors.length) {
      setSelectedVendors(new Set());
    } else {
      setSelectedVendors(new Set(filteredVendors.map((v) => v.id)));
    }
  };

  const handleConfirm = () => {
    if (selectedVendors.size === 0) {
      toast.error('Please select at least one vendor');
      return;
    }
    onConfirm(Array.from(selectedVendors));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" onClick={onClose} />
      <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[calc(90vh/var(--app-scale))] overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Select Vendors</h3>
              <p className="text-sm text-gray-600 mt-1">
                Select vendors to send regularization links
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-6 flex-1 overflow-hidden flex flex-col">
            {/* Search and Select All */}
            <div className="mb-4 flex items-center gap-4">
              <div className="flex-1 relative">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by company name, vendor code, or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {selectedVendors.size === filteredVendors.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {/* Vendor Count */}
            <div className="mb-4 text-sm text-gray-600">
              {selectedVendors.size > 0 && (
                <span className="font-medium text-blue-600">
                  {selectedVendors.size} vendor{selectedVendors.size > 1 ? 's' : ''} selected
                </span>
              )}
              {selectedVendors.size === 0 && (
                <span>{filteredVendors.length} eligible vendors</span>
              )}
            </div>

            {/* Vendor List */}
            <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Icon name="Inbox" size={48} className="mx-auto mb-2 text-gray-400" />
                  <p>No vendors found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredVendors.map((vendor) => (
                    <label
                      key={vendor.id}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVendors.has(vendor.id)}
                        onChange={() => handleToggleVendor(vendor.id)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900">{vendor.company_name}</div>
                        <div className="text-sm text-gray-600 flex items-center gap-4 mt-1">
                          {vendor.vendor_code && (
                            <span className="flex items-center gap-1">
                              <Icon name="Hash" size={14} />
                              {vendor.vendor_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={selectedVendors.size === 0}>
              Send Rectification Links ({selectedVendors.size})
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorSelectionModal;
